import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { useSectionContent } from '../hooks/useSiteContent';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useJsonLd from '../hooks/useJsonLd';
import { JOB_POSTING_LEASE_ON } from '../seo/schemaOrg';
import { ArrowRight, DollarSign, ClipboardList, Plus, ShieldAlert } from 'lucide-react';

const LEASE_ON_DEFAULTS = {
  lease_on_hero_title: 'Lease-On Owner-Operator Program',
  lease_on_hero_subtitle:
    'Run under BlueLock Transport LLC authority. 84% / 16% split on linehaul.',
  lease_on_overview:
    'Lease-On Owner-Operators provide their own truck and lease it onto BlueLock Transport LLC. Drivers run under our USDOT 4557330 / MC 1810883 authority and receive structured dispatch support, weekly settlements, and documented operating agreements.',
  lease_on_compensation:
    'Compensation split: 84% of linehaul revenue to the driver, 16% to BlueLock. Specific deductions (e.g., trailer use, insurance, escrow, plates, fuel programs) are outlined in the operating agreement.',
  lease_on_requirements:
    'Valid CDL-A, current DOT medical card, satisfactory MVR and PSP, minimum verifiable OTR experience as defined by recruiting, current truck registration and inspection, and proof of physical damage / occupational accident coverage as required.',
  lease_on_accessorials:
    'Additional compensation may be available for detention, layover, driver assist, multiple stops, TONU, and specialized services when documented and approved. Accessorial pay is not guaranteed.',
  lease_on_disclaimer:
    'No income is guaranteed. All program terms, deductions, and obligations are defined in the executed operating agreement.',
};

const Section = ({ Icon, title, body, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-3 mb-3">
      <Icon className="w-6 h-6 text-blue-900" />
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
    </div>
    <p className="text-gray-700 leading-relaxed">{body}</p>
  </div>
);

const LeaseOnPage = () => {
  const { content } = useSectionContent('lease_on', LEASE_ON_DEFAULTS);
  useDocumentTitle({
    title: 'Lease-On Owner-Operator Program | BlueLock Freights',
    description:
      'Lease-On with BlueLock Transport LLC: 84% of linehaul to the driver, 16% to BlueLock. Weekly settlements, structured dispatch support, documented operating agreements.',
  });
  useJsonLd('job-lease-on', JOB_POSTING_LEASE_ON);

  return (
    <div className="min-h-screen bg-white" data-testid="lease-on-page">
      <PublicNavbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-xs font-medium uppercase tracking-wide text-blue-200 mb-3">
            Program
          </span>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
            data-testid="lease-on-hero-title"
          >
            {content.lease_on_hero_title}
          </h1>
          <p
            className="text-lg sm:text-xl text-blue-100 max-w-3xl"
            data-testid="lease-on-hero-subtitle"
          >
            {content.lease_on_hero_subtitle}
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <Section
            Icon={ClipboardList}
            title="Program Overview"
            body={content.lease_on_overview}
            testid="lease-on-overview"
          />
          <Section
            Icon={DollarSign}
            title="Compensation"
            body={content.lease_on_compensation}
            testid="lease-on-compensation"
          />
          <Section
            Icon={ShieldAlert}
            title="Driver Requirements"
            body={content.lease_on_requirements}
            testid="lease-on-requirements"
          />
          <Section
            Icon={Plus}
            title="Accessorial Pay"
            body={content.lease_on_accessorials}
            testid="lease-on-accessorials"
          />

          <div
            className="p-6 bg-gray-50 border border-gray-200 rounded-xl"
            data-testid="lease-on-disclaimer"
          >
            <p className="text-sm text-gray-600">{content.lease_on_disclaimer}</p>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Link
              to="/apply"
              className="inline-flex items-center bg-blue-900 hover:bg-blue-800 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              data-testid="lease-on-cta-apply"
            >
              Apply for Lease-On <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <Link
              to="/programs"
              className="inline-flex items-center text-blue-900 font-medium hover:text-blue-700 px-6 py-3"
              data-testid="lease-on-link-programs"
            >
              Compare programs
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default LeaseOnPage;
